import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { clientApi, ApiError } from '../lib/api';
import type { ClientBundle, ResolvedBundleItem } from '../../shared/types';
import { formatDuration } from '../../shared/runtime';
import { bundleItemChip, computeBundleProgress, type BundleProgress } from '../../shared/bundleChip';
import { bundleErrorCopy, type BundleErrorKind } from '../lib/bundleErrorCopy';
import { useForceTheme } from '../lib/theme';
import Wordmark from '../components/Wordmark';
import Eyebrow from '../components/Eyebrow';
import Button from '../components/Button';
import ContactLine from '../components/ContactLine';
import Confetti from '../components/Confetti';
import BundleChipBadge from '../components/BundleChipBadge';
import BundleErrorState from '../components/BundleErrorState';

type ErrorState = BundleErrorKind | 'error' | null;

const CELEBRATED_KEY_PREFIX = 'hsp_bundle_celebrated_';

/**
 * Client hub — /b/:bundleSlug. Landing page for a script bundle: Henry's
 * intro note, aggregate progress, and every script card in order. See
 * docs/script-bundles-plan.md §5 and mockups/script-bundles.html §03/§05/§07.
 *
 * kind === 'shoot' bundles render the dark, read-only shoot-day pack framing
 * (mockups/script-bundles.html §06) instead: no status chips, no progress
 * ring, no celebration — just every script numbered in order, all tappable,
 * opening ClientInterviewView (via BundleScriptView) rather than ClientReview.
 * The split is a branch through this same component (theme + a handful of
 * conditionals), not a fork — every card/error/skeleton sub-component below
 * is shared and restyles dark for free off the semantic-token CSS variables
 * once the root theme flips (see src/client/lib/theme.ts). Kind is read
 * fresh off the bundle fetch on every mount, so nothing here caches it —
 * a bundle PATCHed review↔shoot just renders the other experience next load.
 */
export default function BundleHub() {
  const { bundleSlug } = useParams<{ bundleSlug: string }>();
  const [bundle, setBundle] = useState<ClientBundle | null>(null);
  const [error, setError] = useState<ErrorState>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const isShoot = bundle?.kind === 'shoot';
  useForceTheme(isShoot ? 'dark' : 'light');
  // Once we have good data on screen, a later refetch failure (e.g. the
  // focus-triggered poll below hitting a network blip) must not blow away
  // what's already rendered — only the initial load (or an explicit
  // Try-again click, which resets this) should ever surface the full-screen
  // error state.
  const hasLoadedRef = useRef(false);

  const loadBundle = useCallback(() => {
    if (!bundleSlug) return;
    clientApi
      .getBundle(bundleSlug)
      .then((b) => {
        hasLoadedRef.current = true;
        setBundle(b);
        setError(null);
      })
      .catch((err) => {
        if (hasLoadedRef.current) return;
        if (err instanceof ApiError && err.status === 410) setError('replaced');
        else if (err instanceof ApiError && err.status === 404) setError('not_found');
        else if (err instanceof ApiError && err.status === 423) setError('unavailable');
        else setError('error');
      });
  }, [bundleSlug]);

  // Initial load (and reload if the URL's bundleSlug itself changes) — resets
  // the "have we ever loaded" guard so a genuinely new bundle gets its own
  // fair shot at showing an error page.
  useEffect(() => {
    hasLoadedRef.current = false;
    setBundle(null);
    loadBundle();
  }, [loadBundle]);

  useEffect(() => {
    document.title = bundle ? `${bundle.title} · Sheahan Studios` : 'Sheahan Studios';
  }, [bundle]);

  // Refetch on focus so an approval made elsewhere (another tab, or the
  // switcher's "Back to all scripts") is reflected immediately in the hub's
  // chips/progress — same "simplest correct thing" as ClientReview's own
  // stale-tab handling, just triggered by focus instead of a comment poll.
  useEffect(() => {
    function onFocus() {
      loadBundle();
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadBundle]);

  const progress = bundle ? computeBundleProgress(bundle.items) : null;

  // The full celebration (banner + confetti + the once-ever localStorage
  // mark) fires only once the bundle is genuinely finished — every ready
  // script approved AND nothing still coming. If coming-soon items remain,
  // progress.allApproved can still be true (100% of what's here today), but
  // celebrating now would contradict the "N more on the way" note right next
  // to it — and marking localStorage now would rob the *real* completion,
  // once the last script lands and is approved, of its own celebration.
  // Shoot packs never celebrate — there's no approval concept to complete.
  useEffect(() => {
    if (!progress || !progress.celebrate || !bundleSlug || isShoot) return;
    const key = `${CELEBRATED_KEY_PREFIX}${bundleSlug}`;
    try {
      if (localStorage.getItem(key) === '1') return;
      localStorage.setItem(key, '1');
    } catch {
      // No storage (private mode quirks) — celebrate anyway; worst case it
      // replays on a later visit, which is a much smaller sin than never
      // celebrating at all.
    }
    setShowConfetti(true);
  }, [progress, bundleSlug, isShoot]);

  if (error === 'error') {
    return (
      <BundleErrorState
        eyebrow="// SOMETHING WENT WRONG"
        heading="We couldn’t load these scripts"
        body="It’s most likely temporary: your connection, or a brief hiccup on our end."
        action={
          <div className="mt-6">
            <Button
              variant="primary"
              glow
              onClick={() => {
                setError(null);
                loadBundle();
              }}
            >
              Try again
            </Button>
          </div>
        }
      />
    );
  }

  if (error) {
    return <BundleErrorState {...bundleErrorCopy(error)} />;
  }

  if (!bundle || !progress) {
    return (
      <main className="min-h-screen bg-page">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-20 pb-24">
          <div className="h-3 w-32 bg-card rounded-sm mb-8 skeleton" />
          <div className="h-14 w-2/3 bg-card rounded-sm mb-10 skeleton" />
          <div className="space-y-3">
            <div className="h-4 bg-card rounded-sm skeleton" />
            <div className="h-4 bg-card rounded-sm w-5/6 skeleton" />
            <div className="h-4 bg-card rounded-sm w-4/6 skeleton" />
          </div>
        </div>
      </main>
    );
  }

  if (bundle.items.length === 0) {
    return (
      <BundleErrorState
        eyebrow="// COMING SOON"
        heading="Your scripts are on their way"
        body="Nothing’s quite ready to show you yet. Check back soon."
      />
    );
  }

  return (
    <main data-context="client" className="min-h-screen bg-page text-fg">
      <header className="border-b border-line">
        <div className="max-w-3xl mx-auto px-6 md:px-12 h-16 flex items-center">
          <Wordmark />
        </div>
      </header>

      <section className="relative overflow-hidden">
        {!isShoot && showConfetti && <Confetti />}
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-16">
          <Eyebrow accent>
            {isShoot
              ? `Shoot pack · ${progress.readyCount} script${progress.readyCount === 1 ? '' : 's'}`
              : progress.celebrate
              ? '// SCRIPT BUNDLE · ALL APPROVED'
              : `// SCRIPT BUNDLE · ${progress.readyCount} SCRIPT${progress.readyCount === 1 ? '' : 'S'}`}
          </Eyebrow>
          <h1
            className="font-display font-medium leading-[1.08] tracking-[-0.02em] mt-3 mb-1"
            style={{ fontSize: 'clamp(1.9rem, 5vw, 2.9rem)' }}
          >
            {bundle.title}
          </h1>
          {/* Shoot packs use the mockup's plain-prose subtitle ("For X ·
              everything you need, in order") instead of review's mono/uppercase
              "Prepared for X" — the copy reads as a crew handoff note, not a
              status label. Gracefully drops the "For X" clause (not the whole
              line) when there's no client name on file. */}
          {isShoot ? (
            <p className="text-muted text-[15px] mt-2 mb-6">
              {bundle.client_name ? (
                <>For <span className="text-fg">{bundle.client_name}</span> · everything you need, in order</>
              ) : (
                'Everything you need, in order'
              )}
            </p>
          ) : (
            bundle.client_name && (
              <p className="text-muted text-[13px] uppercase font-mono tracking-[0.08em] mt-2 mb-6">
                Prepared for <span className="text-fg">{bundle.client_name}</span>
              </p>
            )
          )}

          {bundle.intro_note && <HenryNote note={bundle.intro_note} />}

          {/* No progress ring, no status chips, no celebration in a shoot
              pack — there's nothing to approve, just scripts to open. */}
          {!isShoot && progress.celebrate && (
            <CelebrationCard readyCount={progress.readyCount} title={bundle.title} />
          )}

          {!isShoot && <ProgressBlock progress={progress} />}

          <div className="mt-6">
            {bundle.items.map((item, i) =>
              item.kind === 'script' ? (
                <ScriptCard
                  key={item.slug}
                  item={item}
                  index={i}
                  bundleSlug={bundleSlug!}
                  kind={isShoot ? 'shoot' : 'review'}
                />
              ) : (
                <ComingSoonCard key={`soon-${i}`} item={item} index={i} />
              ),
            )}
          </div>

          <div className="mt-12">
            <ContactLine className="mb-4" />
            <div className="flex items-center justify-between text-faint">
              <span className="font-mono text-[11px] uppercase tracking-[0.12em]">
                Sheahan Studios · Confidential
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.12em]">
                // {bundleSlug}
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// ---------- Sub-components ----------

function HenryNote({ note }: { note: string }) {
  return (
    <div className="flex gap-3.5 bg-card border border-line border-l-[3px] border-l-gold rounded-r-md rounded-l-[2px] px-5 py-4 mt-5 mb-7 shadow-flare">
      <span className="shrink-0 w-9 h-9 rounded-full bg-gold text-white font-display font-semibold text-[15px] flex items-center justify-center">
        H
      </span>
      <div className="min-w-0">
        <p className="font-display italic font-normal text-fg text-[15px] leading-[1.5]">{note}</p>
        <span className="block mt-2 font-mono text-[10px] uppercase tracking-[0.1em] font-semibold text-gold">
          Henry
        </span>
      </div>
    </div>
  );
}

function CelebrationCard({ readyCount, title }: { readyCount: number; title: string }) {
  const noun = readyCount === 1 ? 'script' : 'scripts';
  const verb = readyCount === 1 ? 'is' : 'are';
  return (
    <div className="border border-success bg-success/10 rounded-md px-6 py-6 mb-6">
      <div className="text-[26px] leading-none" aria-hidden>
        🎉
      </div>
      <h2 className="font-display font-semibold text-[20px] text-fg mt-3 mb-1.5 leading-[1.2]">
        All scripts approved. We’re ready to shoot.
      </h2>
      <p className="text-muted text-[14px] leading-[1.55]">
        All {readyCount} {noun} for {title} {verb} signed off. I’ll be in touch about the shoot.
      </p>
    </div>
  );
}

function ProgressBlock({ progress }: { progress: BundleProgress }) {
  if (progress.readyCount === 0 && progress.comingSoonCount === 0) return null;
  const pct =
    progress.readyCount === 0 ? 0 : Math.round((progress.approvedCount / progress.readyCount) * 100);
  return (
    <div className="mt-2 mb-2">
      {progress.readyCount > 0 && (
        <>
          <div className="flex items-baseline justify-between gap-4 mb-2.5 flex-wrap">
            <Eyebrow>Your progress</Eyebrow>
            <span className="font-display font-semibold text-[19px] text-fg">
              <b className="text-gold font-semibold">{progress.approvedCount}</b> of{' '}
              <b className="text-gold font-semibold">{progress.readyCount}</b> approved
            </span>
          </div>
          <div className="h-2 rounded-full bg-elevated overflow-hidden">
            <div
              className={`h-full rounded-full ${
                progress.celebrate ? 'bg-gradient-to-r from-accent to-gold' : 'bg-accent'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </>
      )}
      {progress.comingSoonCount > 0 && (
        <p className="text-faint text-[13px] mt-2.5">{progress.comingSoonCount} more on the way</p>
      )}
    </div>
  );
}

function ScriptCard({
  item,
  index,
  bundleSlug,
  kind,
}: {
  item: Extract<ResolvedBundleItem, { kind: 'script' }>;
  index: number;
  bundleSlug: string;
  /** 'shoot' drops the status chip and the "needs your attention" accent
   *  treatment — there's no review status to draw the eye to, every card
   *  reads the same way. */
  kind: 'review' | 'shoot';
}) {
  const chip = kind === 'review' ? bundleItemChip(item) : null;
  // The mockup's ".bcard.needs" treatment — the one card most likely to need
  // the client's attention today gets the loudest styling.
  const needsAttention = chip?.tone === 'accent';
  return (
    <Link
      to={`/b/${bundleSlug}/${item.slug}`}
      className={`
        flex items-center gap-4 bg-card rounded-md px-5 py-4 mt-3 first:mt-0
        transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]
        hover:border-accent hover:-translate-y-px
        ${needsAttention ? 'border-[1.5px] border-accent bg-accent/5' : 'border border-line'}
      `}
    >
      <span className="font-mono text-[11px] text-faint shrink-0 w-5">{index + 1}</span>
      <div className="min-w-0 flex-1">
        {/* Title row. The brief tag sits inline after the title on a wide
            card and wraps under it on a phone, so a card carrying a runtime
            line and a status chip still reads top to bottom. */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span
            className={`font-display font-semibold text-fg ${needsAttention ? 'text-[17px]' : 'text-[16px]'}`}
          >
            {item.title}
          </span>
          {item.template_key === 'interview_brief' && (
            <span className="px-1.5 py-px rounded-full border border-line font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
              Interview brief
            </span>
          )}
        </div>
        {item.runtime && (
          <div className="mt-1 inline-flex items-center gap-2 text-[12px] text-muted">
            <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" aria-hidden />
            {formatDuration(item.runtime.seconds)} on camera
          </div>
        )}
        {chip && (
          <div className="mt-2">
            <BundleChipBadge chip={chip} />
          </div>
        )}
      </div>
      <span
        className={`text-[20px] leading-none shrink-0 ${needsAttention ? 'text-accent' : 'text-faint'}`}
        aria-hidden
      >
        ›
      </span>
    </Link>
  );
}

function ComingSoonCard({
  item,
  index,
}: {
  item: Extract<ResolvedBundleItem, { kind: 'coming_soon' }>;
  index: number;
}) {
  const chip = bundleItemChip(item);
  return (
    <div className="flex items-center gap-4 bg-elevated border border-dashed border-line rounded-md px-5 py-4 mt-3 first:mt-0">
      <span className="font-mono text-[11px] text-faint shrink-0 w-5">{index + 1}</span>
      <div className="min-w-0 flex-1">
        <div className="font-display font-semibold text-muted text-[16px]">{item.title}</div>
        <div className="mt-2">
          <BundleChipBadge chip={chip} />
        </div>
      </div>
    </div>
  );
}
